import { useId, useState, type ComponentProps, type MouseEvent } from 'react';
import { Popover } from 'radix-ui';
import { XIcon } from 'lucide-react';
import { useChatCopy } from '@/lib/chat-i18n';
import { isWorkspaceLink } from '@/lib/workspace-links';
import { useOptionalWorkspace } from '@/workspace/context';
import { parseWorkspaceRoute } from '@/workspace/navigation';
import { sourceCanvasTarget } from '@/workspace/source-canvas-target';
import { CitationExcerptView } from './citation-excerpt';
import { citationTarget, type CitationTarget } from './reference';

function isPlainClick(event: MouseEvent) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

function CitationPreview({
  target,
  children,
}: {
  target: CitationTarget;
  children: React.ReactNode;
}) {
  const copy = useChatCopy();
  const labelId = useId();
  const [open, setOpen] = useState(false);
  const ws = useOptionalWorkspace();
  // In the workspace the cited source opens in the canvas beside the chat; the
  // link stays a real anchor for open-in-new-tab and for surfaces without one.
  const openSource = (event: MouseEvent<HTMLAnchorElement>) => {
    setOpen(false);
    const canvas = parseWorkspaceRoute(new URL(target.href, window.location.href).hash).canvas;
    if (!ws || !canvas || !isPlainClick(event)) return;
    event.preventDefault();
    ws.openCanvas(canvas);
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className="pc-citation" title={copy.citationTitle}>
          <span>{children}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="pc-citation-preview pc-portal"
          side="bottom"
          align="start"
          sideOffset={10}
          collisionPadding={12}
          aria-labelledby={labelId}
        >
          <header className="pc-citation-header">
            <span id={labelId}>{copy.citationTitle}</span>
            <Popover.Close className="pc-citation-close" aria-label={copy.close}>
              <XIcon size={16} />
            </Popover.Close>
          </header>
          <CitationExcerptView target={target} onOpenSource={openSource} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function CitationLink({ href, children, ...props }: ComponentProps<'a'>) {
  const ws = useOptionalWorkspace();
  const target = citationTarget(href);
  if (target)
    return (
      <CitationPreview key={target.endpoint} target={target}>
        {children}
      </CitationPreview>
    );
  // A source opens in the canvas beside the chat, with a back path; a modified
  // click, and surfaces without a workspace, keep the real link.
  const openSource = (event: MouseEvent<HTMLAnchorElement>) => {
    props.onClick?.(event);
    if (!ws || event.defaultPrevented || !isPlainClick(event)) return;
    const canvas = sourceCanvasTarget(href, typeof children === 'string' ? children : undefined);
    if (!canvas) return;
    event.preventDefault();
    ws.openCanvas(canvas);
  };
  return (
    <a
      {...props}
      href={href}
      target={isWorkspaceLink(href) ? '_self' : '_blank'}
      rel="noopener noreferrer"
      onClick={openSource}
    >
      {children}
    </a>
  );
}
